import React from "react";
import {Toggle} from "@rescui/toggle";
import {useSetting} from "../../contexts/setting";
import {usePixelationPalette} from "hooks/usePixelationPalette";
import ColorPicker from "../ColorPicker";
import "./index.css";

// CSS mix-blend-mode values worth exposing for the mosaic. Kept short — the
// long tail (hue/saturation/color/luminosity) rarely reads well over a photo.
const BLEND_MODES = [
  "normal",
  "multiply",
  "screen",
  "overlay",
  "darken",
  "lighten",
  "color-dodge",
  "hard-light",
  "soft-light",
  "difference",
  "exclusion",
];

type SegmentedProps<T extends string> = {
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (value: T) => void;
};

function Segmented<T extends string>({options, value, onChange}: SegmentedProps<T>) {
  return (
    <div className="pixelation-segmented">
      {options.map(([option, label]) => (
        <button
          key={option}
          type="button"
          className={value === option ? "active" : ""}
          onClick={() => onChange(option)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

type SliderProps = {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  display: string;
  tooltip?: string;
};

function Slider({id, label, min, max, step, value, onChange, display, tooltip}: SliderProps) {
  return (
    <div className="slider-control">
      <label htmlFor={id} data-tooltip={tooltip}>
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="slider"
      />
      <span className="slider-value">{display}</span>
    </div>
  );
}

type Backdrop = "none" | "base" | "color";

/**
 * Toolbar panel for the Releases pixelation overlay. Self-gates: the Toolbar
 * only mounts it for the releases channel, but every control here is harmless
 * when the effect is disabled (the layer renders nothing).
 */
const PixelationControls = () => {
  const {
    bgOption,
    pixelationEnabled,
    editPixelationEnabled,
    pixelationSource,
    editPixelationSource,
    pixelationSizeCoefficient,
    editPixelationSizeCoefficient,
    pixelationOpacity,
    editPixelationOpacity,
    pixelationCoverage,
    editPixelationCoverage,
    pixelationDirection,
    editPixelationDirection,
    pixelationGradual,
    editPixelationGradual,
    pixelationOpacityRamp,
    editPixelationOpacityRamp,
    pixelationBlend,
    editPixelationBlend,
    pixelationClickAction,
    editPixelationClickAction,
    pixelationTileShape,
    editPixelationTileShape,
    pixelationTileRoundness,
    editPixelationTileRoundness,
    pixelationTileRadius,
    editPixelationTileRadius,
    pixelationShapes,
    resetPixelationShapes,
    pixelationPalettes,
    setPixelationPalette,
    resetPixelationPalettes,
    regeneratePixelation,
    pixelationShape,
    editPixelationShape,
    pixelationDotPattern,
    editPixelationDotPattern,
    pixelationDotSizeCoefficient,
    editPixelationDotSizeCoefficient,
    pixelationDotScale,
    editPixelationDotScale,
    pixelationDotTone,
    editPixelationDotTone,
    pixelationDotInvert,
    editPixelationDotInvert,
    pixelationDotColor,
    editPixelationDotColor,
    pixelationDotStructure,
    editPixelationDotStructure,
    pixelationDotBackdrop,
    editPixelationDotBackdrop,
    pixelationMasks,
    resetPixelationMasks,
    pixelationBrush,
    editPixelationBrush,
    pixelationBrushSize,
    editPixelationBrushSize,
    pixelationBrushSoftness,
    editPixelationBrushSoftness,
  } = useSetting();

  // The effective palette for the active source — the user's edited override
  // if one exists, else the resolved default (sampled bg / brand colors).
  // Editing any swatch writes the whole array back under the active source, so
  // the first edit seeds from these exact colors and each source's edits
  // persist independently.
  const {palette} = usePixelationPalette();
  const anyCustomized = Object.values(pixelationPalettes).some((p) => Array.isArray(p) && p.length);
  const anyMask = Object.keys(pixelationMasks).length > 0;
  const anyClickedShape = Object.keys(pixelationShapes).length > 0;
  const isDots = pixelationShape === "dots";
  // Image colours take no palette, so the palette rows go away.
  const showPalette = !isDots || pixelationDotColor === "palette";
  const backdrop: Backdrop =
    pixelationDotBackdrop === "none" || pixelationDotBackdrop === "base"
      ? pixelationDotBackdrop
      : "color";

  // Swatch mutators. Each produces the next array from the current effective
  // palette and stores it under the active source.
  const updateSwatch = (i: number, hex: string) =>
    setPixelationPalette(
      pixelationSource,
      palette.map((c, idx) => (idx === i ? hex : c))
    );
  const addSwatch = () =>
    setPixelationPalette(pixelationSource, [...palette, palette[palette.length - 1] || "#888888"]);
  const removeSwatch = (i: number) =>
    setPixelationPalette(
      pixelationSource,
      palette.filter((_, idx) => idx !== i)
    );

  const chooseBackdrop = (next: Backdrop) => {
    if (next === "color") {
      if (backdrop !== "color") editPixelationDotBackdrop("#000000");
    } else {
      editPixelationDotBackdrop(next);
    }
  };

  return (
    <div className="pixelation-controls">
      <div className="pixelation-header">
        <span className="label">Pixelation</span>
        <Toggle
          checked={pixelationEnabled}
          className="font-toggle"
          onChange={(e) => editPixelationEnabled(e.target.checked)}
        />
      </div>

      {pixelationEnabled && (
        <div className="pixelation-body">
          {/* Shape: the edge mosaic of squares, or a halftone of dots. */}
          <div className="pixelation-row">
            <span className="pixelation-row-label">Shape</span>
            <Segmented
              options={[
                ["squares", "Squares"],
                ["dots", "Dots"],
              ]}
              value={pixelationShape}
              onChange={editPixelationShape}
            />
          </div>

          {/* Dots: the colour of the image under each dot, or the palette. */}
          {isDots && (
            <div className="pixelation-row">
              <span
                className="pixelation-row-label"
                data-tooltip="Image: each dot takes the colour of the background under it. Palette: the colours below"
              >
                Color
              </span>
              <Segmented
                options={[
                  ["image", "Image"],
                  ["palette", "Palette"],
                ]}
                value={pixelationDotColor}
                onChange={editPixelationDotColor}
              />
            </div>
          )}

          {showPalette && (
            <>
              {/* Palette source */}
              <div className="pixelation-row">
                <span className="pixelation-row-label">Palette</span>
                <Segmented
                  options={[
                    ["background", "Background"],
                    ["product", "Product"],
                  ]}
                  value={pixelationSource}
                  onChange={editPixelationSource}
                />
              </div>

              {/* Palette swatches — editable for both Background and Product. Pick
              a color to change a swatch, × to remove, + to add. The first edit
              of a source seeds from its resolved default; edits persist per
              source. Reset clears all overrides back to the defaults. */}
              <div className="pixelation-swatches">
                {palette.map((color, i) => (
                  <div className="pixelation-swatch" key={i}>
                    <ColorPicker
                      value={color}
                      onChange={(hex) => updateSwatch(i, hex)}
                      className="pixelation-swatch-input"
                      ariaLabel={`Color ${i + 1}`}
                    />
                    <button
                      type="button"
                      className="pixelation-swatch-remove"
                      onClick={() => removeSwatch(i)}
                      disabled={palette.length <= 1}
                      title={
                        palette.length <= 1
                          ? "The palette needs at least one color"
                          : "Remove color"
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="pixelation-swatch-add"
                  onClick={addSwatch}
                  title="Add color"
                >
                  +
                </button>
              </div>

              {/* Reset palette customizations (both sources) back to defaults. */}
              {anyCustomized && (
                <button
                  type="button"
                  className="pixelation-palette-reset"
                  onClick={resetPixelationPalettes}
                  title="Discard edited colors and restore the default palettes"
                >
                  Reset colors
                </button>
              )}
            </>
          )}

          {isDots ? (
            <>
              {/* What sets the size of each dot. */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="Image: bright areas of the background give large dots. Random: dots of different sizes in random structures"
                >
                  Pattern
                </span>
                <Segmented
                  options={[
                    ["image", "Image"],
                    ["random", "Random"],
                  ]}
                  value={pixelationDotPattern}
                  onChange={editPixelationDotPattern}
                />
              </div>
              {(pixelationDotPattern === "image" || pixelationDotColor === "image") &&
                bgOption === "generative" && (
                  <p className="pixelation-hint">
                    The generative background has no image to read. Choose a picture background, or
                    the Random pattern with Palette colours.
                  </p>
                )}

              <Slider
                id="pix-dot-grid"
                label="Grid"
                tooltip="Distance between dots, relative to the title size"
                min={0.05}
                max={1}
                step={0.01}
                value={pixelationDotSizeCoefficient}
                onChange={editPixelationDotSizeCoefficient}
                display={`${Number(pixelationDotSizeCoefficient).toFixed(2)}×`}
              />
              <Slider
                id="pix-dot-scale"
                label="Dot"
                tooltip="Largest dot, % of the grid cell (100% = dots touch)"
                min={10}
                max={200}
                step={1}
                value={pixelationDotScale}
                onChange={editPixelationDotScale}
                display={`${pixelationDotScale}%`}
              />
              <Slider
                id="pix-dot-tone"
                label="Tone"
                tooltip="Make all dots larger (+) or smaller (−)"
                min={-100}
                max={100}
                step={1}
                value={pixelationDotTone}
                onChange={editPixelationDotTone}
                display={`${pixelationDotTone > 0 ? "+" : ""}${pixelationDotTone}`}
              />
              {pixelationDotPattern === "random" && (
                <Slider
                  id="pix-dot-structure"
                  label="Structure"
                  tooltip="Size of the random structures, in dots"
                  min={1}
                  max={40}
                  step={1}
                  value={pixelationDotStructure}
                  onChange={editPixelationDotStructure}
                  display={String(pixelationDotStructure)}
                />
              )}
              {pixelationDotPattern === "image" && (
                <div className="pixelation-row">
                  <span
                    className="pixelation-row-label"
                    data-tooltip="Dark areas of the background give the large dots"
                  >
                    Invert
                  </span>
                  <Toggle
                    checked={pixelationDotInvert}
                    className="font-toggle"
                    onChange={(e) => editPixelationDotInvert(e.target.checked)}
                  />
                </div>
              )}

              {/* What fills the area behind the dots. */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="Behind the dots: nothing (the image stays), the base colour, or a colour"
                >
                  Backdrop
                </span>
                <Segmented
                  options={[
                    ["none", "None"],
                    ["base", "Base"],
                    ["color", "Color"],
                  ]}
                  value={backdrop}
                  onChange={chooseBackdrop}
                />
                {backdrop === "color" && (
                  <ColorPicker
                    value={pixelationDotBackdrop}
                    onChange={editPixelationDotBackdrop}
                    className="pixelation-swatch-input"
                    ariaLabel="Backdrop color"
                  />
                )}
              </div>

              <Slider
                id="pix-opacity"
                label="Opacity"
                min={0}
                max={100}
                step={1}
                value={pixelationOpacity}
                onChange={editPixelationOpacity}
                display={`${pixelationOpacity}%`}
              />
            </>
          ) : (
            <>
              {/* Square size coefficient */}
              <Slider
                id="pix-size-coef"
                label="Square"
                min={0.3}
                max={3}
                step={0.01}
                value={pixelationSizeCoefficient}
                onChange={editPixelationSizeCoefficient}
                display={`${pixelationSizeCoefficient.toFixed(2)}×`}
              />

              {/* Tile shape: plain squares, circles, or round corners by chance. */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="Square tiles, circles, or a mix: each corner of a tile is round by chance"
                >
                  Tiles
                </span>
                <Segmented
                  options={[
                    ["square", "Square"],
                    ["circle", "Circle"],
                    ["mixed", "Mixed"],
                  ]}
                  value={pixelationTileShape}
                  onChange={editPixelationTileShape}
                />
              </div>
              {pixelationTileShape === "mixed" && (
                <Slider
                  id="pix-tile-roundness"
                  label="Round"
                  tooltip="The chance that a corner of a tile is round"
                  min={0}
                  max={100}
                  step={1}
                  value={pixelationTileRoundness}
                  onChange={editPixelationTileRoundness}
                  display={`${pixelationTileRoundness}%`}
                />
              )}
              {(pixelationTileShape !== "square" || anyClickedShape) && (
                <Slider
                  id="pix-tile-radius"
                  label="Radius"
                  tooltip="The radius of a round corner: 50% gives circles, 100% gives quarter discs on one corner"
                  min={5}
                  max={100}
                  step={1}
                  value={pixelationTileRadius}
                  onChange={editPixelationTileRadius}
                  display={`${pixelationTileRadius}%`}
                />
              )}
              <Slider
                id="pix-opacity"
                label="Opacity"
                min={0}
                max={100}
                step={1}
                value={pixelationOpacity}
                onChange={editPixelationOpacity}
                display={`${pixelationOpacity}%`}
              />
              <Slider
                id="pix-coverage"
                label="Coverage"
                min={5}
                max={100}
                step={1}
                value={pixelationCoverage}
                onChange={editPixelationCoverage}
                display={`${pixelationCoverage}%`}
              />

              {/* The edge the mosaic grows from, for every banner. A locked
                  template can still rotate its own mosaic 90° at a time via the
                  rotate control in its tile-size popover (see
                  PixelDirectionControl). */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="The edge the mosaic grows from; the coverage is measured from it"
                >
                  From
                </span>
                <Segmented
                  options={[
                    ["ltr", "Left"],
                    ["ttb", "Top"],
                    ["rtl", "Right"],
                    ["btt", "Bottom"],
                  ]}
                  value={pixelationDirection}
                  onChange={editPixelationDirection}
                />
              </div>

              {/* Gradual scatter-fade toward the coverage edge (the direction each
                  template fades — left→right by default, or its rotated direction). */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="Squares thin out toward the coverage edge (the direction the template's mosaic fades)"
                >
                  Fade
                </span>
                <Toggle
                  checked={pixelationGradual}
                  className="font-toggle"
                  onChange={(e) => editPixelationGradual(e.target.checked)}
                />
              </div>

              {/* Smooth opacity ramp toward the coverage edge (orientation-aware). */}
              <div className="pixelation-row">
                <span
                  className="pixelation-row-label"
                  data-tooltip="Each step toward the coverage edge gets more transparent until it dissolves (the direction the template's mosaic fades)"
                >
                  Opacity fade
                </span>
                <Toggle
                  checked={pixelationOpacityRamp}
                  className="font-toggle"
                  onChange={(e) => editPixelationOpacityRamp(e.target.checked)}
                />
              </div>
            </>
          )}

          {/* Blend mode */}
          <div className="pixelation-row">
            <span className="pixelation-row-label">Blend</span>
            <select
              className="pixelation-select"
              value={pixelationBlend}
              onChange={(e) => editPixelationBlend(e.target.value)}
            >
              {BLEND_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </div>

          {/* Click action — what clicking a square in the banner does */}
          {!isDots && (
            <div className="pixelation-row">
              <span
                className="pixelation-row-label"
                data-tooltip="What clicking a square does: cycle its opacity, cycle its color through the palette, or change its shape (a corner: round or sharp; the centre: circle or square)"
              >
                Click
              </span>
              <Segmented
                options={[
                  ["opacity", "Opacity"],
                  ["color", "Color"],
                  ["shape", "Shape"],
                ]}
                value={pixelationClickAction}
                onChange={editPixelationClickAction}
              />
            </div>
          )}
          {!isDots && pixelationClickAction === "shape" && (
            <p className="pixelation-hint">
              Click near a corner of a square to make it round or sharp. Click its centre for a
              circle.
            </p>
          )}
          {!isDots && anyClickedShape && (
            <button
              type="button"
              className="pixelation-palette-reset"
              onClick={resetPixelationShapes}
              title="The clicked tiles take the shape of the Tiles setting again"
            >
              Reset shapes
            </button>
          )}

          {/* Brush mask: paint where the effect shows on each banner. */}
          <div className="pixelation-row">
            <span
              className="pixelation-row-label"
              data-tooltip="Paint on a banner where the effect shows (Paint) or goes (Erase). Hold Alt for the other tool"
            >
              Brush
            </span>
            <Segmented
              options={[
                ["off", "Off"],
                ["show", "Paint"],
                ["hide", "Erase"],
              ]}
              value={pixelationBrush}
              onChange={editPixelationBrush}
            />
          </div>
          {pixelationBrush !== "off" && (
            <>
              <Slider
                id="pix-brush-size"
                label="Size"
                min={10}
                max={400}
                step={1}
                value={pixelationBrushSize}
                onChange={editPixelationBrushSize}
                display={`${pixelationBrushSize}px`}
              />
              <Slider
                id="pix-brush-softness"
                label="Softness"
                min={0}
                max={100}
                step={1}
                value={pixelationBrushSoftness}
                onChange={editPixelationBrushSoftness}
                display={`${pixelationBrushSoftness}%`}
              />
              <p className="pixelation-hint">
                Alt paints with the other tool. Undo, Fill and Clear for one banner are next to its
                controls.
              </p>
            </>
          )}
          {anyMask && (
            <button
              type="button"
              className="pixelation-palette-reset"
              onClick={resetPixelationMasks}
              title="Remove the brush masks of all banners, so the effect shows everywhere"
            >
              Reset masks
            </button>
          )}

          {/* Regenerate */}
          <button
            type="button"
            className="pixelation-regenerate"
            onClick={regeneratePixelation}
            title={
              isDots
                ? "Reshuffle colors and the random pattern"
                : "Reshuffle colors and transparency"
            }
          >
            <span className="pixelation-regenerate-icon">⟳</span> Regenerate
          </button>
        </div>
      )}
    </div>
  );
};

export default PixelationControls;
